import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'

import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import {
  isInternalCampaign,
  isReadOnlyCampaign
} from '@/ducks/campaigns/campaigns.helpers'
import type { Campaign, FoodOfferCampaign } from '@/ducks/campaigns/campaigns.types'
import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'

import BuyXGetYDetails from './BuyXGetYDetails'
import CampaignDetailsModalFooter from './CampaignDetailsModalFooter'
import CampaignDetailsReadOnlyNote from './CampaignDetailsReadOnlyNote'
import CampaignDetailsSummary from './CampaignDetailsSummary'
import CartDiscountDetails from './CartDiscountDetails'
import ItemDiscountDetails from './ItemDiscountDetails'
import Styles from './CampaignDetailsModal.module.scss'
import useFoodOfferCampaignDetails from './useFoodOfferCampaignDetails'

type Props = {
  campaign: Campaign | null
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignsQueryParams?: FetchCampaignsQueryParams
}

const renderTypeDetails = (
  campaign: Campaign,
  foodOfferDetails: ReturnType<typeof useFoodOfferCampaignDetails>
) => {
  switch (campaign.campaignType) {
    case 'cart_discount':
      return <CartDiscountDetails campaign={campaign} />
    case 'food-offer':
      return (
        <ItemDiscountDetails
          item={foodOfferDetails?.itemDetail}
          isLoading={foodOfferDetails?.isLoading}
        />
      )
    case 'buyXGetYOffer':
      return <BuyXGetYDetails campaign={campaign} />
    default:
      return null
  }
}

const CampaignDetailsModal = ({
  campaign,
  open,
  onOpenChange,
  campaignsQueryParams
}: Props) => {
  const { t } = useTranslation()
  const isFoodOffer = campaign?.campaignType === 'food-offer'
  const foodOfferDetails = useFoodOfferCampaignDetails(
    isFoodOffer ? (campaign as FoodOfferCampaign) : null,
    open && isFoodOffer
  )

  const handleClose = () => onOpenChange(false)
  const showFooterActions = campaign && !isReadOnlyCampaign(campaign)
  const showReadOnlyNote = campaign && isInternalCampaign(campaign)

  const footer = showFooterActions ? (
    <CampaignDetailsModalFooter
      campaign={campaign}
      onClose={handleClose}
      campaignsQueryParams={campaignsQueryParams}
    />
  ) : showReadOnlyNote ? (
    <CampaignDetailsReadOnlyNote />
  ) : null

  const body = campaign ? (
    <div className={Styles.bodyContent} data-testid="campaign-details-modal">
      <CampaignDetailsSummary
        campaign={campaign}
        discountValue={
          isFoodOffer ? foodOfferDetails?.discountLabel : undefined
        }
        expirationValue={
          isFoodOffer ? foodOfferDetails?.expirationLabel : undefined
        }
      />
      {renderTypeDetails(campaign, foodOfferDetails)}
    </div>
  ) : null

  return (
    <CommonModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('marketing.campaigns.details.title')}
      body={body}
      footer={footer}
      width={508}
      scrollableBody
      withFooterSeparator={!!(showFooterActions || showReadOnlyNote)}
      className={clsx(Styles.modal, Styles.headerOverride)}
    />
  )
}

export default CampaignDetailsModal
